import {useEffect, useMemo, useState} from "react";
import {useIsTabVisible} from "../../../hooks/useIsTabVisible";
import {Milliseconds} from "../../../constans/milliseconds";
import {watchStream} from "../../../api/camera/watchStream";
import {getStreamUrlTimeout} from "../../../utils/CameraUtils";
import {isUsingFakeData} from "../../../utils/DemoUtils";

const REFRESH_URL_TIME = getStreamUrlTimeout();
const ERROR_RETRY_TIME = Milliseconds.MINUTE;
const NO_FRAGMENTS_RETRY_TIME = 5 * Milliseconds.SECOND;

/**
 * Custom hook to fetch HLS streaming URL for a given stream ID.
 *
 * @param {string} streamId
 * @return {{ url: string | null, error: boolean, loading: boolean }}
 */
const useHLSStreamingURL = (streamId) => {
    const demo = useMemo(() => isUsingFakeData(), []);

    const [url, setURL] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [refreshTime, setRefreshTime] = useState(Date.now());
    const [intervalTime, setIntervalTime] = useState(REFRESH_URL_TIME);

    const isTabVisible = useIsTabVisible();
    const hasStreamId = Boolean(streamId);

    useEffect(() => {
        if (!isTabVisible || !hasStreamId) return;

        const intervalId = setInterval(() => {
            setRefreshTime(Date.now());
        }, intervalTime);

        return () => clearInterval(intervalId);
    }, [isTabVisible, intervalTime, hasStreamId]);

    useEffect(() => {
        if (!demo) {
            return;
        }
        setURL(require("../../../resources/video/demo-cctv.webm"));
        setError(false);
        setMessage(null);
        setLoading(false);
    }, [demo]);

    useEffect(() => {
        if (demo) {
            return;
        }
        if (!streamId) {
            setURL(null);
            setError(false);
            setMessage("No streamId provided");
            setLoading(false);
            return;
        }

        const fetchStream = async () => {
            setLoading(true);
            setMessage(null);
            try {
                const response = await watchStream({streamId});
                setURL(response.streamingSessionURL);
                setError(false);
                setIntervalTime(REFRESH_URL_TIME);
            } catch (e) {
                console.error(e);
                const isEmptyStream = e?.message?.includes(
                    "No fragments found in the stream for the streaming request."
                );
                setMessage(
                    isEmptyStream
                        ? "No fragments found in the stream for the streaming request"
                        : (e?.message ?? "Stream error")
                );
                setError(true);
                setURL(null);
                setIntervalTime(
                    isEmptyStream ? NO_FRAGMENTS_RETRY_TIME : ERROR_RETRY_TIME
                );
            } finally {
                setLoading(false);
            }
        };

        fetchStream();
    }, [refreshTime, streamId, demo]);

    return useMemo(
        () => ({
            url,
            error,
            loading,
            message,
        }),
        [url, error, loading, message]
    );
};

export default useHLSStreamingURL;
